import { themeZafire } from '@zafire/highcharts';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/highcharts-more';
import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmountCurrencyFirst } from '../../../app/utils/renderUtils';
import { LIQUIDITY_CALCULATOR_CHART_HEIGHT_PX } from '../../config/constants';
import { useLiquidityChartAutoSize } from '../../hooks';
import type { ILiquidityCalculationValues } from '../../interfaces';
import { formatMillions } from '../../utils';

Highcharts.setOptions(themeZafire.create());

const DATA_LABEL_GAP_PX = 4;

const MIN_BAR_HEIGHT_PX = 4;

/** Font size used when the chart is captured for the client-package PDF (wide canvas, then scaled down). */
const EXPORT_FONT_SIZE = '35px';

// Highcharts sets `below` on column/waterfall points at runtime; it is not on the Point type.
type WaterfallPointLabelContext = Highcharts.Point & { below?: boolean };

// Highcharts floors near-zero bars to `minPointLength` but centers them on the step boundary, so a
// tiny bar straddles the boundary and a negative one appears to point up. Re-anchor each floored bar
// to extend in its step's direction (down for a decrease, up for an increase). Zero bars have no
// direction and are left centered.
const applyDirectionalMinBars = (chart: Highcharts.Chart): void => {
    const points = chart.series?.[0]?.points ?? [];

    for (const point of points) {
        const shape = point.shapeArgs;
        const graphic = point.graphic;
        const stepValue = point.y ?? 0;
        if (!shape || !graphic || stepValue === 0) continue;

        const height = shape.height ?? 0;
        // Only re-anchor bars Highcharts floored to the minimum; taller bars already show their direction.
        if (height > MIN_BAR_HEIGHT_PX + 0.5) continue;

        const boundary = (shape.y ?? 0) + height / 2;
        const y = stepValue < 0 ? boundary : boundary - MIN_BAR_HEIGHT_PX;
        graphic.attr({ y, height: MIN_BAR_HEIGHT_PX });
    }
};

interface ILiquidityCalculatorGraphProps {
    calculationData: ILiquidityCalculationValues;
    /**
     * When true, uses larger, pixel-fixed fonts tuned for the client-package PDF export, where the
     * chart is captured on a wide canvas and then scaled down. Keeps on-screen sizing unaffected.
     */
    exportMode?: boolean;
}

export const LiquidityCalculatorGraph: FunctionComponent<ILiquidityCalculatorGraphProps> = ({ calculationData, exportMode = false }) => {
    const { t } = useTranslation();
    const { chartRef, containerRef, chartHeight } = useLiquidityChartAutoSize(LIQUIDITY_CALCULATOR_CHART_HEIGHT_PX);
    const { cashWithJb, cashWithOtherBanks, liquidityNeed, cashBuffer, currency, availableInvestmentAmount } = calculationData;

    // Re-anchor floored near-zero bars after every (re)draw
    const handleChartCreated = useCallback((chart: Highcharts.Chart) => {
        Highcharts.addEvent(chart, 'render', () => applyDirectionalMinBars(chart));
        applyDirectionalMinBars(chart);
    }, []);

    const options = useMemo<Highcharts.Options>(
        () => ({
            chart: {
                type: 'waterfall',
                backgroundColor: 'transparent',
                height: chartHeight,
                spacing: exportMode ? [32, 20, 28, 24] : [24, 16, 16, 16],
            },
            accessibility: { enabled: false },
            credits: { enabled: false },
            exporting: { enabled: false },
            title: { text: '' },
            legend: { enabled: false },
            tooltip: {
                outside: true,
                backgroundColor: '#ffffff',
                borderWidth: 0,
                borderRadius: 4,
                // eslint-disable-next-line react-hooks/unsupported-syntax -- Highcharts tooltip formatter API
                formatter: function (this) {
                    const signedAmount = this.options.custom?.signedAmount as number | undefined;
                    const amount = signedAmount ?? Math.abs((this.y ?? 0) as number);
                    const name = this.name ?? '';

                    return `<span style="color: var(--fg-color-default-emphasis); font-size: var(--type-label-small-font-size); line-height: var(--type-label-small-line-height);">${name}</span><br/><span style="color: var(--fg-color-default-normal); font-size: var(--type-body-small-font-size); line-height: var(--type-body-small-line-height);"><b>${formatAmountCurrencyFirst(amount, true, currency)}</b></span>`;
                },
            },
            xAxis: {
                type: 'category',
                lineColor: 'var(--border-color-default-normal)',
                tickLength: 0,
                gridLineColor: 'var(--border-color-default-normal)',
                gridLineDashStyle: 'Dot',
                gridLineWidth: 1,
                labels: {
                    // Export uses plain SVG labels (reliably captured by html-to-image); on-screen uses
                    // HTML labels so each label can wrap within its category slot on narrow layouts.
                    useHTML: !exportMode,
                    formatter: exportMode
                        ? undefined
                        : function (this) {
                              const axis = this.axis as Highcharts.Axis & { width: number };
                              const slotWidth = axis.width / this.axis.series[0].data.length;
                              const styles = ['white-space:normal', 'text-align:center', 'margin:0 auto'];

                              if (slotWidth > 0) {
                                  styles.unshift(`min-width:${Math.floor(slotWidth)}px`);
                                  styles.unshift(`max-width:${Math.floor(slotWidth)}px`);
                              }

                              return `<div style="${styles.join(';')}">${this.value}</div>`;
                          },
                    style: exportMode
                        ? { color: 'var(--fg-color-default-normal)', fontSize: EXPORT_FONT_SIZE, fontWeight: '400' }
                        : {
                              color: 'var(--fg-color-default-normal)',
                              fontSize: 'var(--type-body-medium-font-size)',
                              fontWeight: 'var(--type-body-medium-regular-font-weight)',
                              lineHeight: 'var(--type-body-medium-line-height)',
                          },
                },
            },
            yAxis: {
                title: {
                    text: t('liquidityCalculatorGraph.yAxisLabel', { currency }),
                    ...(exportMode ? { style: { fontSize: EXPORT_FONT_SIZE } } : {}),
                },
                gridLineColor: 'var(--border-color-default-normal)',
                gridLineDashStyle: 'Dot',
                gridLineWidth: 1,
                plotLines: [
                    {
                        value: 0,
                        color: 'var(--border-color-default-emphasis)',
                        width: 2,
                        zIndex: 4,
                    },
                ],
                labels: {
                    formatter: function (this) {
                        return formatMillions(this.value as number);
                    },
                    style: exportMode
                        ? { color: 'var(--zf-charts-general-axis-title)', fontSize: EXPORT_FONT_SIZE }
                        : { color: 'var(--zf-charts-general-axis-title)' },
                },
            },
            plotOptions: {
                waterfall: {
                    pointPadding: 0.1,
                    borderWidth: 0,
                    minPointLength: MIN_BAR_HEIGHT_PX,
                    lineColor: 'var(--zf-table-ag-grid-text-color)',
                    lineWidth: 1.5,
                    dashStyle: 'Dot',
                    dataLabels: {
                        enabled: true,
                        inside: false,
                        align: 'center',
                        verticalAlign: 'bottom',
                        allowOverlap: true,
                        crop: false,
                        overflow: 'allow',
                        style: {
                            color: 'var(--fg-color-default-emphasis)',
                            fontSize: exportMode ? EXPORT_FONT_SIZE : '12px',
                            fontWeight: '600',
                            textOutline: 'none',
                        },
                        formatter: function (this: WaterfallPointLabelContext, labelOptions: Highcharts.DataLabelsOptions) {
                            const amount = (this.y ?? 0) as number;
                            const barHeight = this.shapeArgs?.height ?? 0;

                            labelOptions.inside = false;
                            labelOptions.verticalAlign = 'bottom';
                            labelOptions.y = (this.below ? -barHeight : 0) - DATA_LABEL_GAP_PX;

                            const signedAmount = this.options.custom?.signedAmount as number | undefined;

                            return formatMillions(signedAmount ?? Math.abs(amount));
                        },
                    },
                },
            },
            series: [
                {
                    type: 'waterfall',
                    name: 'liquidityCalculator',
                    data: [
                        {
                            name: t('liquidityCalculatorGraph.bar.cashJb'),
                            y: cashWithJb.amount,
                            color: 'var(--color-data-viz-primary-bold)',
                            custom: { signedAmount: cashWithJb.amount },
                        },
                        {
                            name: t('liquidityCalculatorGraph.bar.cashExternal'),
                            y: cashWithOtherBanks.amount,
                            color: 'var(--color-data-viz-primary-emphasis)',
                            custom: { signedAmount: cashWithOtherBanks.amount },
                        },
                        {
                            name: exportMode ? `${t('liquidityCalculatorGraph.bar.liquidityNeed')} *` : t('liquidityCalculatorGraph.bar.liquidityNeed'),
                            y: 0 - liquidityNeed.amount,
                            color: 'var(--color-data-viz-alternative-one-bold)',
                        },
                        {
                            name: t('liquidityCalculatorGraph.bar.cashBuffer'),
                            y: 0 - cashBuffer,
                            color: 'var(--color-data-viz-alternative-one-bold)',
                        },
                        {
                            name: t('liquidityCalculatorGraph.bar.availableInvestmentAmount'),
                            y: 0 - availableInvestmentAmount,
                            color: 'var(--color-data-viz-primary-stronger)',
                            custom: { signedAmount: availableInvestmentAmount },
                        },
                    ],
                },
            ],
        }),
        [t, currency, cashWithJb.amount, cashWithOtherBanks.amount, liquidityNeed.amount, cashBuffer, availableInvestmentAmount, chartHeight, exportMode],
    );

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            <HighchartsReact
                ref={chartRef}
                highcharts={Highcharts}
                options={options}
                callback={handleChartCreated}
                containerProps={{ style: { width: '100%', height: '100%' } }}
            />
        </div>
    );
};
